import { useState, useEffect } from 'react';

/**
 * Final step after processing + downloading a batch: send the confirmation
 * email to production/fulfillment. Deliberately a button, not automatic —
 * it should go out only once the CSV/zip are actually downloaded.
 */
export default function EmailConfirmation({
  batches,
  batchDate,
  secretKey,
  onNeedAuth,
  onAuthFailed,
  onSent,
  disabled,
  disabledReason,
}) {
  const [preview, setPreview] = useState(null);
  const [shipBy, setShipBy] = useState(''); // YYYY-MM-DD, seeded from the preview default
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/notify/preview?batchDate=${batchDate}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setPreview(d);
        if (d.shipByDate) setShipBy(d.shipByDate);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [batchDate]);

  // Weekday label + weekend warning for whatever date is currently picked
  const shipByParts = shipBy ? shipBy.split('-').map(Number) : null;
  const shipByDay = shipByParts ? new Date(Date.UTC(shipByParts[0], shipByParts[1] - 1, shipByParts[2])) : null;
  const shipByWeekday = shipByDay
    ? shipByDay.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
    : null;
  const isWeekend = shipByDay ? [0, 6].includes(shipByDay.getUTCDay()) : false;
  const isCustom = !!(preview?.shipByDate && shipBy && shipBy !== preview.shipByDate);

  const handleSend = async () => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/notify/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: secretKey, batches, batchDate, shipBy: shipBy || undefined }),
      });
      const d = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (!res.ok) throw new Error(d.error || 'Failed to send email');
      if (d.sent === false && d.skipped) {
        setError(d.skipped);
        return;
      }
      setSent(d);
      if (onSent) onSent(); // lets the dashboard clear the missing-email flag
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 w-full border border-gray-200 rounded-xl px-4 py-3.5">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-900">Final step: confirmation email</p>
          <p className={`text-[11px] mt-0.5 truncate ${preview?.sandboxTo ? 'text-gray-400 line-through' : 'text-gray-400'}`}>
            {preview ? preview.recipients.join(', ') : 'Loading recipients...'}
          </p>
        </div>
        {sent ? null : (
          <div className="flex-shrink-0 text-right">
            <label className="block text-[11px] text-gray-500 mb-0.5" htmlFor="shipBy">
              Ship by
            </label>
            <input
              id="shipBy"
              type="date"
              value={shipBy}
              onChange={(e) => setShipBy(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-lg text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <p className={`text-[10px] mt-0.5 ${isWeekend ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
              {shipByWeekday
                ? isWeekend
                  ? `${shipByWeekday} — weekend`
                  : `${shipByWeekday}${isCustom ? ' · custom' : ''}`
                : ' '}
            </p>
          </div>
        )}
        {sent ? (
          <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Sent
          </span>
        ) : (
          <button
            onClick={handleSend}
            disabled={sending || !preview || disabled}
            title={disabled ? disabledReason : undefined}
            className="px-3.5 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        )}
      </div>
      {preview && preview.sandboxTo && !sent && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
          <span className="font-semibold">Sandbox mode:</span> the team will NOT be emailed — everything
          routes to <span className="font-mono">{preview.sandboxTo}</span>. Remove{' '}
          <span className="font-mono">EMAIL_SANDBOX_TO</span> in Vercel to send for real.
        </p>
      )}
      {preview && preview.willSkip && !sent && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
          <span className="font-semibold">No email will be sent:</span> {preview.willSkip}
        </p>
      )}
      {preview && preview.configured === false && !sent && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
          Email isn't configured yet — add the RESEND_API_KEY env var to enable sending.
        </p>
      )}
      {error && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2">{error}</p>
      )}
      {sent && (
        <p className="text-[11px] text-emerald-700 mt-2">
          {sent.sandboxed
            ? `Sandbox send — delivered only to ${sent.recipients.join(', ')}, not the team.`
            : `Confirmation sent to ${sent.recipients.length} recipients — ship by ${sent.shipBy}.`}
          {sent.attachments?.length > 0 && (
            <> Attached: <span className="font-mono">{sent.attachments.join(', ')}</span>.</>
          )}
        </p>
      )}
      {sent && sent.attachmentNotes?.length > 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
          {sent.attachmentNotes.join(' · ')}
        </p>
      )}
    </div>
  );
}
